import { parseClientAddress } from "./ip-address"

type NewsUrl = {
  protocol: string
  username: string
  password: string
  port: string
  hostname: string
}

type NewsUrlConstructor = new (input: string) => NewsUrl

const newsUrlConstructor: NewsUrlConstructor | undefined = (globalThis as unknown as { URL?: NewsUrlConstructor }).URL

export const NEWS_GENRES = ["IT", "ゲーム", "ガジェット", "映画", "宇宙", "科学", "動物", "その他"] as const

export type NewsGenre = (typeof NEWS_GENRES)[number]

export type NewsThreadMetadata = {
  articleUrl: string
  publisher: string
  publishedAt: string
  imageUrl: string
  genre: NewsGenre
}

const NON_PUBLIC_HOST_SUFFIXES = [
  ".localhost",
  ".local",
  ".internal",
  ".home.arpa",
  ".lan",
  ".test",
  ".invalid",
  ".example",
  ".onion",
] as const

const NON_PUBLIC_IPV4_RANGES = [
  ["0.0.0.0", 8],
  ["10.0.0.0", 8],
  ["100.64.0.0", 10],
  ["127.0.0.0", 8],
  ["169.254.0.0", 16],
  ["172.16.0.0", 12],
  ["192.0.0.0", 24],
  ["192.0.2.0", 24],
  ["192.88.99.0", 24],
  ["192.168.0.0", 16],
  ["198.18.0.0", 15],
  ["198.51.100.0", 24],
  ["203.0.113.0", 24],
  ["224.0.0.0", 4],
  ["240.0.0.0", 4],
] as const

function ipv4Number(address: string): number | null {
  const parsed = parseClientAddress(address)
  if (parsed?.family !== "ipv4") {
    return null
  }
  const octets = parsed.value.split(".").map(Number)
  return (((octets[0] ?? 0) << 24) | ((octets[1] ?? 0) << 16) | ((octets[2] ?? 0) << 8) | (octets[3] ?? 0)) >>> 0
}

function isNonPublicIpv4(address: string): boolean {
  const value = ipv4Number(address)
  if (value === null) {
    return true
  }
  return NON_PUBLIC_IPV4_RANGES.some(([network, prefixLength]) => {
    const networkValue = ipv4Number(network)
    if (networkValue === null) {
      return true
    }
    const mask = (0xffffffff << (32 - prefixLength)) >>> 0
    return (value & mask) >>> 0 === (networkValue & mask) >>> 0
  })
}

function ipv6Groups(address: string): number[] | null {
  const parsed = parseClientAddress(address)
  if (parsed?.family !== "ipv6") {
    return null
  }
  const [head = "", tail = ""] = parsed.value.includes("::") ? parsed.value.split("::") : [parsed.value, ""]
  const headGroups = head.length > 0 ? head.split(":") : []
  const tailGroups = tail.length > 0 ? tail.split(":") : []
  if (headGroups.length + tailGroups.length > 8) {
    return null
  }
  return [
    ...headGroups.map((group) => Number.parseInt(group, 16)),
    ...Array.from({ length: 8 - headGroups.length - tailGroups.length }, () => 0),
    ...tailGroups.map((group) => Number.parseInt(group, 16)),
  ]
}

function isNonPublicIpv6(address: string): boolean {
  const groups = ipv6Groups(address)
  if (groups === null || groups.length !== 8) {
    return true
  }
  if (groups.every((group) => group === 0) || (groups.slice(0, 7).every((group) => group === 0) && groups[7] === 1)) {
    return true
  }
  const first = groups[0] ?? 0
  const second = groups[1] ?? 0
  const isIpv4Translated = groups.slice(0, 4).every((group) => group === 0) && groups[4] === 0xffff && groups[5] === 0
  if (
    (first & 0xfe00) === 0xfc00 || // unique-local
    (first & 0xffc0) === 0xfe80 || // link-local
    (first & 0xffc0) === 0xfec0 || // deprecated site-local
    (first & 0xff00) === 0xff00 || // multicast
    (first === 0x0064 && second === 0xff9b && (groups[2] ?? 0) === 0x0001) || // local-use NAT64
    (first === 0x0100 && second === 0) || // discard-only
    (first === 0x2001 && second < 0x0200) || // IETF special-purpose assignments
    (first === 0x2001 && second === 0x0db8) || // documentation
    (first === 0x3fff && second < 0x1000) || // documentation (3fff::/20)
    first === 0x5f00 || // SRv6 SIDs
    isIpv4Translated || // IPv4-translated addresses
    first === 0x2002 // 6to4 embeds an IPv4 address in the URL
  ) {
    return true
  }

  const isIpv4Mapped = groups.slice(0, 5).every((group) => group === 0) && groups[5] === 0xffff
  if (isIpv4Mapped) {
    const high = groups[6] ?? 0
    const low = groups[7] ?? 0
    return isNonPublicIpv4(`${high >>> 8}.${high & 0xff}.${low >>> 8}.${low & 0xff}`)
  }
  const isIpv4Compatible = groups.slice(0, 6).every((group) => group === 0)
  if (isIpv4Compatible) {
    return true
  }

  // The well-known NAT64 prefix carries an IPv4 address in the last 32 bits.
  if (first === 0x0064 && second === 0xff9b && groups.slice(2, 6).every((group) => group === 0)) {
    const high = groups[6] ?? 0
    const low = groups[7] ?? 0
    return isNonPublicIpv4(`${high >>> 8}.${high & 0xff}.${low >>> 8}.${low & 0xff}`)
  }
  return false
}

/** News article and image URLs are displayed by clients and must not target local/private hosts. */
export function isPublicHttpsNewsUrl(input: string): boolean {
  let url: NewsUrl
  try {
    if (newsUrlConstructor === undefined) {
      return false
    }
    url = new newsUrlConstructor(input)
  } catch {
    return false
  }
  if (url.protocol !== "https:" || url.username !== "" || url.password !== "" || url.port === "0") {
    return false
  }

  const hostnameWithOptionalFinalDot = url.hostname.replace(/^\[|\]$/g, "").toLowerCase()
  const hostname = hostnameWithOptionalFinalDot.endsWith(".")
    ? hostnameWithOptionalFinalDot.slice(0, -1)
    : hostnameWithOptionalFinalDot
  if (hostname.length === 0 || hostname.endsWith(".") || hostname.includes("..")) {
    return false
  }
  if (hostname.includes(":")) {
    return !isNonPublicIpv6(hostname)
  }
  if (!hostname.includes(".")) {
    return false
  }
  if (NON_PUBLIC_HOST_SUFFIXES.some((suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix))) {
    return false
  }
  if (/^\d+(?:\.\d+){0,3}$/.test(hostname)) {
    return !isNonPublicIpv4(hostname)
  }
  return true
}
